import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiGetContent, apiMe, apiUpdateContent, apiResetContent, clearToken, getToken } from "@/lib/api";
import { Save, LogOut, RefreshCw, ExternalLink, ChevronDown, ChevronRight } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { useContent } from "@/context/ContentContext";
import { ImageField } from "@/components/admin/ImageField";

const Field = ({ label, value, onChange, type = "text", textarea }) => (
  <div className="grid gap-1.5">
    <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">{label}</label>
    {textarea ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={7}
        className="bg-midnight/70 border border-gold/20 px-4 py-3 text-ivory text-base leading-relaxed focus:outline-none focus:border-gold resize-y min-h-[180px]"
      />
    ) : (
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="bg-midnight/70 border border-gold/20 px-4 py-3 text-ivory text-base focus:outline-none focus:border-gold"
      />
    )}
  </div>
);

const Section = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="glass border border-gold/15">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-5 text-left"
      >
        <span className="font-display text-2xl text-ivory">{title}</span>
        {open ? <ChevronDown className="text-gold" /> : <ChevronRight className="text-gold" />}
      </button>
      {open && <div className="px-6 pb-6 grid gap-5">{children}</div>}
    </div>
  );
};

const ArrayEditor = ({ items, setItems, fields, label, allowImage }) => {
  const add = () => setItems([...items, Object.fromEntries(fields.map((f) => [f.key, ""]))]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const update = (i, k, v) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it));
    setItems(next);
  };
  return (
    <div className="grid gap-4">
      {(items || []).map((it, i) => (
        <div key={i} className="border border-gold/10 p-4 grid gap-3 bg-midnight/40">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.25em] text-gold">{label} #{i + 1}</div>
            <button type="button" onClick={() => remove(i)} className="text-xs text-red-400 hover:text-red-300">Remove</button>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {fields.map((f) =>
              f.key === "image" || f.key === "avatar" ? (
                <ImageField
                  key={f.key}
                  label={f.label}
                  value={it[f.key]}
                  onChange={(v) => update(i, f.key, v)}
                />
              ) : (
                <Field
                  key={f.key}
                  label={f.label}
                  value={it[f.key]}
                  onChange={(v) => update(i, f.key, v)}
                  textarea={f.textarea}
                />
              )
            )}
          </div>
        </div>
      ))}
      <button type="button" onClick={add} className="btn-ghost justify-center">+ Add {label}</button>
    </div>
  );
};

const AdminDashboard = () => {
  const nav = useNavigate();
  const { refresh } = useContent();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login");
      return;
    }
    (async () => {
      try {
        await apiMe();
        const data = await apiGetContent();
        setContent(data);
      } catch (e) {
        clearToken();
        nav("/admin/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  const update = (key, value) => setContent({ ...content, [key]: value });
  const updateField = (key, field, value) => setContent({ ...content, [key]: { ...content[key], [field]: value } });

  const save = async () => {
    setSaving(true);
    try {
      await apiUpdateContent(content);
      await refresh();
      toast.success("Saved. Changes are live.");
    } catch (e) {
      toast.error("Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset all content to defaults? This cannot be undone.")) return;
    await apiResetContent();
    const data = await apiGetContent();
    setContent(data);
    await refresh();
    toast.success("Content reset to defaults.");
  };

  const logout = () => {
    clearToken();
    nav("/admin/login");
  };

  if (loading || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-midnight text-ivory/60">Loading CMS…</div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-midnight">
      <Toaster theme="dark" position="top-right" />
      <header className="border-b border-gold/15 bg-midnight-deep/70 backdrop-blur-md sticky top-0 z-20">
        <div className="wrap py-4 flex items-center justify-between gap-4">
          <div>
            <div className="eyebrow">CMS · Admin</div>
            <h1 className="font-display text-2xl text-ivory">Geomantic Kalpataru</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/blog" target="_blank" className="btn-ghost" data-testid="admin-open-blog">
              Blog CMS
            </Link>
            <Link to="/" target="_blank" className="btn-ghost" data-testid="admin-preview-site">
              <ExternalLink className="h-4 w-4" /> View Site
            </Link>
            <button onClick={resetAll} className="btn-ghost" data-testid="admin-reset">
              <RefreshCw className="h-4 w-4" /> Reset
            </button>
            <button onClick={save} disabled={saving} className="btn-gold disabled:opacity-50" data-testid="admin-save">
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save All"}
            </button>
            <button onClick={logout} className="text-ivory/60 hover:text-gold" data-testid="admin-logout">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="wrap py-10 grid gap-5 max-w-5xl">
        <Section title="Brand & Contact" defaultOpen>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Site Name" value={content.brand?.name} onChange={(v) => updateField("brand", "name", v)} />
            <Field label="Tagline" value={content.brand?.tagline} onChange={(v) => updateField("brand", "tagline", v)} />
            <Field label="WhatsApp Number (e.g. +919999999999)" value={content.brand?.whatsappNumber} onChange={(v) => updateField("brand", "whatsappNumber", v)} />
            <Field label="WhatsApp Community Invite Link (chat.whatsapp.com/...)" value={content.brand?.whatsappCommunity} onChange={(v) => updateField("brand", "whatsappCommunity", v)} />
            <Field label="Default WhatsApp Message" value={content.brand?.whatsappMessage} onChange={(v) => updateField("brand", "whatsappMessage", v)} textarea />
            <Field label="Email" value={content.brand?.email} onChange={(v) => updateField("brand", "email", v)} />
            <Field label="City" value={content.brand?.city} onChange={(v) => updateField("brand", "city", v)} />
            <Field label="Instagram URL" value={content.brand?.instagram} onChange={(v) => updateField("brand", "instagram", v)} />
            <Field label="YouTube URL" value={content.brand?.youtube} onChange={(v) => updateField("brand", "youtube", v)} />
            <Field label="Facebook URL" value={content.brand?.facebook} onChange={(v) => updateField("brand", "facebook", v)} />
          </div>
        </Section>

        <Section title="Hero (Home Page)">
          <Field label="Eyebrow" value={content.hero?.eyebrow} onChange={(v) => updateField("hero", "eyebrow", v)} />
          <Field label="Headline (line 1)" value={content.hero?.headlineLine1} onChange={(v) => updateField("hero", "headlineLine1", v)} />
          <Field label="Headline (line 2 — italic gold)" value={content.hero?.headlineLine2} onChange={(v) => updateField("hero", "headlineLine2", v)} />
          <Field label="Subtitle" value={content.hero?.subtitle} onChange={(v) => updateField("hero", "subtitle", v)} textarea />
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Primary CTA Text" value={content.hero?.primaryCtaText} onChange={(v) => updateField("hero", "primaryCtaText", v)} />
            <Field label="Secondary CTA Text" value={content.hero?.secondaryCtaText} onChange={(v) => updateField("hero", "secondaryCtaText", v)} />
          </div>
          <ImageField label="Background Image" value={content.hero?.image} onChange={(v) => updateField("hero", "image", v)} />
        </Section>

        <Section title="Stats (4 numbers shown on Home)">
          <ArrayEditor
            label="Stat"
            items={content.stats || []}
            setItems={(v) => update("stats", v)}
            fields={[{ key: "n", label: "Number" }, { key: "label", label: "Label" }]}
          />
        </Section>

        <Section title="Home · Highlight / Event / Thought of the Day (below Hero)">
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Show this block? (true/false)" value={String(content.highlight?.visible ?? true)} onChange={(v) => updateField("highlight", "visible", v === "true")} />
            <Field label="Kind (e.g. Thought of the Day / Event / Highlight)" value={content.highlight?.kind} onChange={(v) => updateField("highlight", "kind", v)} />
          </div>
          <Field label="Eyebrow" value={content.highlight?.eyebrow} onChange={(v) => updateField("highlight", "eyebrow", v)} />
          <Field label="Title / Quote" value={content.highlight?.title} onChange={(v) => updateField("highlight", "title", v)} textarea />
          <Field label="Body / Description (optional)" value={content.highlight?.body} onChange={(v) => updateField("highlight", "body", v)} textarea />
          <ImageField label="Image (optional)" value={content.highlight?.image} onChange={(v) => updateField("highlight", "image", v)} />
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Button Text (optional)" value={content.highlight?.ctaText} onChange={(v) => updateField("highlight", "ctaText", v)} />
            <Field label="Button Link (URL, optional)" value={content.highlight?.ctaLink} onChange={(v) => updateField("highlight", "ctaLink", v)} />
          </div>
          <p className="text-xs text-ivory/45">Set <code className="text-gold">Show</code> to <code className="text-gold">false</code> to hide this block. Leave button fields blank to hide the button.</p>
        </Section>

        <Section title="Home · 'What is Geomantic Kalpataru' block">
          <Field label="Eyebrow" value={content.whatIs?.eyebrow} onChange={(v) => updateField("whatIs", "eyebrow", v)} />
          <Field label="Title" value={content.whatIs?.title} onChange={(v) => updateField("whatIs", "title", v)} />
          <Field label="Body paragraph 1" value={content.whatIs?.body1} onChange={(v) => updateField("whatIs", "body1", v)} textarea />
          <Field label="Body paragraph 2" value={content.whatIs?.body2} onChange={(v) => updateField("whatIs", "body2", v)} textarea />
          <Field label="Floating Quote (short)" value={content.whatIs?.quote} onChange={(v) => updateField("whatIs", "quote", v)} textarea />
          <ImageField label="Image" value={content.whatIs?.image} onChange={(v) => updateField("whatIs", "image", v)} />
        </Section>

        <Section title="Home · How It Works (5 steps)">
          <ArrayEditor
            label="Step"
            items={content.howItWorks || []}
            setItems={(v) => update("howItWorks", v)}
            fields={[
              { key: "n", label: "Step number (e.g. 01)" },
              { key: "title", label: "Step title" },
              { key: "body", label: "Step description", textarea: true },
            ]}
          />
        </Section>

        <Section title="Home · Brahma Satya block">
          <Field label="Eyebrow" value={content.brahmaSatyaHome?.eyebrow} onChange={(v) => updateField("brahmaSatyaHome", "eyebrow", v)} />
          <Field label="Title" value={content.brahmaSatyaHome?.title} onChange={(v) => updateField("brahmaSatyaHome", "title", v)} />
          <Field label="Body" value={content.brahmaSatyaHome?.body} onChange={(v) => updateField("brahmaSatyaHome", "body", v)} textarea />
          <Field label="CTA button text" value={content.brahmaSatyaHome?.ctaText} onChange={(v) => updateField("brahmaSatyaHome", "ctaText", v)} />
          <ArrayEditor
            label="Sub-current"
            items={content.brahmaSatyaHome?.items || []}
            setItems={(v) => updateField("brahmaSatyaHome", "items", v)}
            fields={[
              { key: "t", label: "Title" },
              { key: "d", label: "Description", textarea: true },
            ]}
          />
        </Section>

        <Section title="Home · Benefits (6 cards)">
          <ArrayEditor
            label="Benefit"
            items={content.benefits || []}
            setItems={(v) => update("benefits", v)}
            fields={[
              { key: "title", label: "Title" },
              { key: "body", label: "Body", textarea: true },
              { key: "icon", label: "Icon (Heart, Sparkles, TrendingUp, Sun, Droplet, Eye)" },
            ]}
          />
        </Section>

        <Section title="Home · Founder Message">
          <Field label="Eyebrow" value={content.founder?.eyebrow} onChange={(v) => updateField("founder", "eyebrow", v)} />
          <Field label="Headline quote" value={content.founder?.title} onChange={(v) => updateField("founder", "title", v)} textarea />
          <Field label="Body paragraph 1" value={content.founder?.body1} onChange={(v) => updateField("founder", "body1", v)} textarea />
          <Field label="Body paragraph 2" value={content.founder?.body2} onChange={(v) => updateField("founder", "body2", v)} textarea />
          <Field label="Photo badge label" value={content.founder?.badge} onChange={(v) => updateField("founder", "badge", v)} />
          <Field label="CTA button text" value={content.founder?.ctaText} onChange={(v) => updateField("founder", "ctaText", v)} />
          <ImageField label="Founder Photo" value={content.founder?.image} onChange={(v) => updateField("founder", "image", v)} />
        </Section>

        <Section title="Home · Section Titles & Eyebrows">
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="How It Works — Eyebrow" value={content.sections?.howItWorksEyebrow} onChange={(v) => updateField("sections", "howItWorksEyebrow", v)} />
            <Field label="How It Works — Title" value={content.sections?.howItWorksTitle} onChange={(v) => updateField("sections", "howItWorksTitle", v)} />
            <Field label="Benefits — Eyebrow" value={content.sections?.benefitsEyebrow} onChange={(v) => updateField("sections", "benefitsEyebrow", v)} />
            <Field label="Benefits — Title" value={content.sections?.benefitsTitle} onChange={(v) => updateField("sections", "benefitsTitle", v)} />
            <Field label="Testimonials — Eyebrow" value={content.sections?.testimonialsEyebrow} onChange={(v) => updateField("sections", "testimonialsEyebrow", v)} />
            <Field label="Testimonials — Title" value={content.sections?.testimonialsTitle} onChange={(v) => updateField("sections", "testimonialsTitle", v)} />
            <Field label="FAQ — Eyebrow" value={content.sections?.faqEyebrow} onChange={(v) => updateField("sections", "faqEyebrow", v)} />
            <Field label="FAQ — Title" value={content.sections?.faqTitle} onChange={(v) => updateField("sections", "faqTitle", v)} />
            <Field label="Contact — Eyebrow" value={content.sections?.contactEyebrow} onChange={(v) => updateField("sections", "contactEyebrow", v)} />
            <Field label="Contact — Title" value={content.sections?.contactTitle} onChange={(v) => updateField("sections", "contactTitle", v)} textarea />
          </div>
          <Field label="Contact — Body copy" value={content.sections?.contactBody} onChange={(v) => updateField("sections", "contactBody", v)} textarea />
        </Section>

        <Section title="Products">
          <ArrayEditor
            label="Product"
            items={content.products || []}
            setItems={(v) => update("products", v)}
            allowImage
            fields={[
              { key: "name", label: "Name" },
              { key: "slug", label: "URL slug (optional — auto from name)" },
              { key: "priceInr", label: "Price (₹ INR, number only)" },
              { key: "priceUsd", label: "Price ($ USD, number only)" },
              { key: "price", label: "Price label (e.g. ₹100 · One-time)" },
              { key: "body", label: "Description (Markdown supported)", textarea: true },
              { key: "image", label: "Image URL" },
            ]}
          />
          <p className="text-xs text-ivory/45">
            Description supports Markdown formatting. Use <code className="text-gold">- item</code> for bullet points, <code className="text-gold">1. item</code> for numbered lists, <code className="text-gold">**bold**</code>, and <code className="text-gold">*italic*</code>. Blank line = new paragraph.
          </p>
        </Section>

        <Section title="Podcasts">
          <ArrayEditor
            label="Episode"
            items={content.podcasts || []}
            setItems={(v) => update("podcasts", v)}
            allowImage
            fields={[
              { key: "number", label: "Episode #" },
              { key: "title", label: "Title" },
              { key: "host", label: "Host" },
              { key: "guest", label: "Guest" },
              { key: "duration", label: "Duration (e.g. 42 min)" },
              { key: "date", label: "Date (e.g. Feb 2026)" },
              { key: "tag", label: "Tag" },
              { key: "description", label: "Description", textarea: true },
              { key: "image", label: "Cover Image URL" },
              { key: "listenUrl", label: "Listen URL (Spotify etc.)" },
              { key: "youtubeUrl", label: "YouTube URL" },
            ]}
          />
        </Section>

        <Section title="Testimonials / Success Stories">
          <ArrayEditor
            label="Testimonial"
            items={content.testimonials || []}
            setItems={(v) => update("testimonials", v)}
            allowImage
            fields={[
              { key: "name", label: "Client Name" },
              { key: "country", label: "Country" },
              { key: "role", label: "Role / Location (optional)" },
              { key: "service", label: "Service Taken" },
              { key: "rating", label: "Rating (1–5)" },
              { key: "date", label: "Date (YYYY-MM-DD)" },
              { key: "order", label: "Display Order (lower = first)" },
              { key: "featured", label: "Featured on Home? (true/false)" },
              { key: "visible", label: "Visible? (true/false — default true)" },
              { key: "quote", label: "Written Testimonial", textarea: true },
              { key: "avatar", label: "Client Photo (optional)" },
            ]}
          />
          <p className="text-xs text-ivory/45">Tips: Set <code className="text-gold">featured=true</code> to show on Home. Set <code className="text-gold">visible=false</code> to hide a testimonial without deleting it.</p>
        </Section>

        <Section title="Blog Posts">
          <p className="text-sm text-ivory/70">Blog posts have their own editor with rich text, images, categories & SEO fields.</p>
          <a href="/admin/blog" className="btn-gold mt-2 justify-center" data-testid="link-to-blog-admin">
            Open Blog CMS →
          </a>
        </Section>

        <Section title="FAQs">
          <ArrayEditor
            label="FAQ"
            items={content.faqs || []}
            setItems={(v) => update("faqs", v)}
            fields={[
              { key: "q", label: "Question" },
              { key: "a", label: "Answer", textarea: true },
            ]}
          />
        </Section>

        <Section title="Services (tabs on Services page)">
          <ArrayEditor
            label="Service"
            items={content.services || []}
            setItems={(v) => update("services", v)}
            allowImage
            fields={[
              { key: "id", label: "ID (slug, lowercase)" },
              { key: "label", label: "Tab Label" },
              { key: "eyebrow", label: "Eyebrow" },
              { key: "title", label: "Title (wrap a phrase in 'single quotes' to italicise it in gold)" },
              { key: "intro", label: "Intro", textarea: true },
              { key: "points", label: "Points (comma-separated)", textarea: true },
              { key: "image", label: "Image URL" },
            ]}
          />
          <p className="text-xs text-ivory/45">Tip: For Points use comma-separated values like: <em>Personalised mapping, Bespoke yantra, 90-day practice</em>. For italic gold in the title, wrap the phrase in single quotes: <em>The art of 'turning desire' into form.</em></p>
        </Section>

        <Section title="Services Page · Hero + Section headings">
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Hero Eyebrow" value={content.servicesPage?.eyebrow} onChange={(v) => updateField("servicesPage", "eyebrow", v)} />
            <Field label="Hero Title (wrap in 'single quotes' for italic gold)" value={content.servicesPage?.title} onChange={(v) => updateField("servicesPage", "title", v)} />
          </div>
          <Field label="Hero Subtitle" value={content.servicesPage?.subtitle} onChange={(v) => updateField("servicesPage", "subtitle", v)} textarea />
          <ImageField label="Hero Background Image" value={content.servicesPage?.heroImage} onChange={(v) => updateField("servicesPage", "heroImage", v)} />
          <div className="grid gap-3 md:grid-cols-2 mt-2">
            <Field label="Process — Eyebrow" value={content.servicesPage?.processEyebrow} onChange={(v) => updateField("servicesPage", "processEyebrow", v)} />
            <Field label="Process — Title" value={content.servicesPage?.processTitle} onChange={(v) => updateField("servicesPage", "processTitle", v)} />
            <Field label="Benefits — Eyebrow" value={content.servicesPage?.benefitsEyebrow} onChange={(v) => updateField("servicesPage", "benefitsEyebrow", v)} />
            <Field label="Benefits — Title" value={content.servicesPage?.benefitsTitle} onChange={(v) => updateField("servicesPage", "benefitsTitle", v)} />
            <Field label="CTA — Eyebrow" value={content.servicesPage?.ctaEyebrow} onChange={(v) => updateField("servicesPage", "ctaEyebrow", v)} />
            <Field label="CTA — Title (wrap in 'single quotes' for italic)" value={content.servicesPage?.ctaTitle} onChange={(v) => updateField("servicesPage", "ctaTitle", v)} />
            <Field label="CTA — Button Text" value={content.servicesPage?.ctaButton} onChange={(v) => updateField("servicesPage", "ctaButton", v)} />
          </div>
        </Section>

        <div className="sticky bottom-4 z-10 flex justify-end">
          <button onClick={save} disabled={saving} className="btn-gold disabled:opacity-50 shadow-2xl" data-testid="admin-save-bottom">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save All Changes"}
          </button>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
